import { Button } from "@/shared/ui/button";
import { DateField } from "@/shared/ui/date-field";
import { Checkbox } from "@/shared/ui/checkbox";
import { Label } from "@/shared/ui/label";
import { Separator } from "@/shared/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { useLanguage } from "@/shared/hooks/use-language";
import { labelOr } from "@/shared/lib/label";
import {
  EXTERNAL_TRADE_SIDES,
  EXTERNAL_TRADE_STATUSES,
} from "@/features/portfolio/api/external-trade";
import type { TransactionFilters } from "./transaction-filters";

const toggle = <T,>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];

interface TransactionFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: TransactionFilters;
  onDraftChange: (filters: TransactionFilters) => void;
  onApply: () => void;
  onClear: () => void;
  /**
   * Bumped by every call to `clearFilters` in `detail.tsx` — the sheet's own Clear button
   * and the page-level "clear from empty" button both resolve to it. Folded into the two
   * `DateField`s' `key` below, the same pattern `access/index.tsx`'s `historyResetCount`
   * uses: `value` alone cannot force an unparsed entry off the screen when the clear sets
   * it to the same `""` the field already reported (see `DateField`'s own doc comment),
   * and today that gap happens to be masked here by Radix unmounting the sheet's content
   * whenever `clearFilters` also closes it — an unmount this component does not itself
   * rely on, and must not start relying on.
   */
  resetCount: number;
}

export const TransactionFilterSheet = ({
  open,
  onOpenChange,
  draft,
  onDraftChange,
  onApply,
  onClear,
  resetCount,
}: TransactionFilterSheetProps) => {
  const { t } = useLanguage();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[520px] overflow-y-auto"
        data-testid="investor-filter-sheet"
      >
        <SheetHeader>
          <SheetTitle>{t("investorDetail.filterTitle")}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">
              {t("investorDetail.filterType")}
            </p>
            <div className="space-y-3">
              {EXTERNAL_TRADE_SIDES.map((side) => (
                <div key={side} className="flex items-center gap-2">
                  <Checkbox
                    id={`filter-side-${side}`}
                    checked={draft.sides.includes(side)}
                    onCheckedChange={() =>
                      onDraftChange({ ...draft, sides: toggle(draft.sides, side) })
                    }
                  />
                  <Label
                    htmlFor={`filter-side-${side}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {t(`portfolio.side.${side}`)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">
              {t("investorDetail.filterStatus")}
            </p>
            <div className="space-y-3">
              {EXTERNAL_TRADE_STATUSES.map((status) => (
                <div key={status} className="flex items-center gap-2">
                  <Checkbox
                    id={`filter-status-${status}`}
                    checked={draft.statuses.includes(status)}
                    onCheckedChange={() =>
                      onDraftChange({ ...draft, statuses: toggle(draft.statuses, status) })
                    }
                  />
                  <Label
                    htmlFor={`filter-status-${status}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {labelOr(t, `status.${status}`, status)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">
              {t("investorDetail.filterPeriod")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateField
                key={`filter-start-date-${resetCount}`}
                id="filter-start-date"
                label={t("investorDetail.startDate")}
                value={draft.startDate}
                onChange={(startDate) => onDraftChange({ ...draft, startDate })}
              />
              <DateField
                key={`filter-end-date-${resetCount}`}
                id="filter-end-date"
                label={t("investorDetail.endDate")}
                value={draft.endDate}
                onChange={(endDate) => onDraftChange({ ...draft, endDate })}
              />
            </div>
          </div>
        </div>

        <SheetFooter className="gap-2 sm:justify-start">
          <Button onClick={onApply} data-testid="investor-filter-apply">
            {t("investorDetail.applyFilters")}
          </Button>
          <Button variant="outline" onClick={onClear} data-testid="investor-filter-clear">
            {t("investorDetail.clearFilters")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
